import {
  Component, Input, Output, EventEmitter, ViewChild, ElementRef,
  AfterViewInit, OnChanges, SimpleChanges,
} from '@angular/core';
import { Frame, HexColor, COLS, ROWS, idx, OFF } from '../../../models/led-board.model';
import {
  floodFill, normalizeHex, applyCells, cellsForTool, rowCells, colCells, Cell, clampScale,
  shiftFrameWrap, shiftCells, rectFillCells, circleFillCells,
} from '../../../models/led-board.util';

type EditorTool = 'pencil' | 'eraser' | 'eyedropper' | 'fill' | 'row' | 'col' | 'line' | 'rect' | 'circle';

@Component({
  selector: 'app-led-grid-editor',
  templateUrl: './led-grid-editor.component.html',
  styleUrls: ['./led-grid-editor.component.scss'],
  standalone: false,
})
export class LedGridEditorComponent implements AfterViewInit, OnChanges {
  @ViewChild('canvas') canvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('viewport') viewportRef: ElementRef<HTMLElement>;

  @Input() frame: Frame;
  @Output() frameChange = new EventEmitter<Frame>();
  @Input() color: HexColor = '#00ccff';
  @Input() tool: EditorTool = 'pencil';
  @Output() colorPicked = new EventEmitter<HexColor>();
  @Output() strokeStart = new EventEmitter<void>();
  @Input() selectMode = false;
  /** Other people's brushes in a live session: {colour, pixel index}. */
  @Input() cursors: { color: string; cell: number }[] = [];

  readonly cols = COLS;
  readonly rows = ROWS;
  cell = 14;

  selection = new Set<number>();

  // view transform
  scale = 1;
  offX = 0;
  offY = 0;
  private pointers = new Map<number, { x: number; y: number }>();
  // A pinch remembers the point of the DRAWING under the fingers, so the zoom
  // stays anchored there instead of pulling everything toward the top-left.
  private gesture: { dist: number; startScale: number; contentX: number; contentY: number } | null = null;

  readonly minScale = 1;
  readonly maxScale = 8;

  hideZoomHint = (() => {
    try { return localStorage.getItem('lbZoomHintDismissed') === '1'; } catch { return false; }
  })();
  // the frame value this editor itself last emitted — used to tell our own edits
  // apart from external frame changes (e.g. switching the active animation frame).
  private selfFrame: Frame | null = null;

  get transform(): string { return `translate(${this.offX}px, ${this.offY}px) scale(${this.scale})`; }

  /** True once the view is magnified — the zoom readout and Fit button hang off this. */
  get zoomed(): boolean { return this.scale > 1.001; }
  get zoomLabel(): string { return `${this.scale.toFixed(1)}\u00d7`; }
  get canZoomIn(): boolean { return this.scale < this.maxScale - 0.001; }
  get canZoomOut(): boolean { return this.zoomed; }

  fit() { this.scale = 1; this.offX = 0; this.offY = 0; this.clampView(); }

  /** Viewport and unscaled canvas sizes; null before the view exists (unit tests). */
  private viewMetrics(): { vw: number; vh: number; bw: number; bh: number } | null {
    const vp = this.viewportRef?.nativeElement;
    const cv = this.canvasRef?.nativeElement;
    if (!vp || !cv) { return null; }
    const vw = vp.clientWidth, vh = vp.clientHeight;
    // offsetWidth/Height are layout sizes: the CSS transform doesn't touch them.
    const bw = cv.offsetWidth || vw, bh = cv.offsetHeight || vh;
    if (!vw || !vh || !bw || !bh) { return null; }
    return { vw, vh, bw, bh };
  }

  /**
   * Keep the drawing where people can reach it: no empty gutters while it is
   * bigger than the viewport, and centred when it is smaller. Without this you
   * can flick the board off screen and the only way back is the Fit button.
   */
  clampView() {
    const m = this.viewMetrics();
    if (!m) { return; }
    const cw = m.bw * this.scale, chh = m.bh * this.scale;
    this.offX = cw <= m.vw ? (m.vw - cw) / 2 : Math.min(0, Math.max(m.vw - cw, this.offX));
    this.offY = chh <= m.vh ? (m.vh - chh) / 2 : Math.min(0, Math.max(m.vh - chh, this.offY));
  }

  /** Zoom by `factor` about a point given in viewport coordinates. */
  zoomAtPoint(px: number, py: number, factor: number) {
    const next = clampScale(this.scale * factor, this.minScale, this.maxScale);
    const contentX = (px - this.offX) / this.scale;
    const contentY = (py - this.offY) / this.scale;
    this.scale = next;
    this.offX = px - contentX * next;
    this.offY = py - contentY * next;
    this.clampView();
    this.noteZoomUsed();
  }

  /** Button zoom: keep the middle of the viewport put. */
  zoomBy(factor: number) {
    const m = this.viewMetrics();
    this.zoomAtPoint(m ? m.vw / 2 : 0, m ? m.vh / 2 : 0, factor);
  }

  onWheel(ev: WheelEvent) {
    const vp = this.viewportRef?.nativeElement;
    if (!vp) { return; }
    ev.preventDefault();
    const r = vp.getBoundingClientRect();
    this.zoomAtPoint(ev.clientX - r.left, ev.clientY - r.top, ev.deltaY < 0 ? 1.12 : 1 / 1.12);
  }

  /** The hint has done its job the moment someone zooms. */
  noteZoomUsed() {
    if (this.hideZoomHint) { return; }
    this.hideZoomHint = true;
    try { localStorage.setItem('lbZoomHintDismissed', '1'); } catch {}
  }

  nudge(dx: number, dy: number) {
    if (this.selection.size) {
      const r = shiftCells(this.frame, this.selection, dx, dy);
      this.frame = r.frame;
      this.selection = r.selection;
    } else {
      this.frame = shiftFrameWrap(this.frame, dx, dy);
    }
    this.selfFrame = this.frame;
    this.frameChange.emit(this.frame);
    this.redraw();
  }
  clearSelection() { this.selection = new Set<number>(); this.redraw(); }

  private addToSelection(cells: Cell[]) {
    for (const c of cells) { this.selection.add(idx(c.x, c.y)); }
    this.redraw();
  }
  private removeFromSelection(cells: Cell[]) {
    for (const c of cells) { this.selection.delete(idx(c.x, c.y)); }
    this.redraw();
  }
  private selectionCellsForTool(sx: number, sy: number, ex: number, ey: number): Cell[] {
    if (this.tool === 'rect') { return rectFillCells(sx, sy, ex, ey); }
    if (this.tool === 'circle') { return circleFillCells(sx, sy, ex, ey); }
    return [];
  }
  private previewSelection(cells: Cell[]) {
    this.redraw();
    const ctx = this.canvasRef?.nativeElement.getContext('2d');
    if (!ctx) { return; }
    ctx.fillStyle = 'rgba(0, 204, 255, 0.4)';
    for (const c of cells) { ctx.fillRect(c.x * this.cell, c.y * this.cell, this.cell, this.cell); }
  }

  private centroid(): { x: number; y: number } {
    let sx = 0, sy = 0;
    this.pointers.forEach(p => { sx += p.x; sy += p.y; });
    const n = this.pointers.size || 1;
    return { x: sx / n, y: sy / n };
  }
  /** The pinch centre in viewport coordinates, which is what the transform uses. */
  private centroidLocal(): { x: number; y: number } {
    const c = this.centroid();
    const vp = this.viewportRef?.nativeElement;
    if (!vp) { return c; }
    const r = vp.getBoundingClientRect();
    return { x: c.x - r.left, y: c.y - r.top };
  }
  private spread(): number {
    const pts = Array.from(this.pointers.values());
    if (pts.length < 2) { return 0; }
    return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
  }

  private active = false;
  private startPt: Cell | null = null;
  private lastPt: Cell | null = null;

  ngAfterViewInit() { this.redraw(); }
  ngOnChanges(changes: SimpleChanges) {
    // An external frame change (switching the active frame, undo/redo, load) — i.e.
    // not the echo of our own edit — invalidates the selection, which is indexed
    // against the frame it was drawn on. Clear it so we never move stale indices.
    if (changes['frame'] && !changes['frame'].firstChange && this.frame !== this.selfFrame) {
      this.selection = new Set<number>();
    }
    if (changes['cursors'] && !changes['cursors'].firstChange && this.canvasRef) { this.redraw(); }
    if (this.canvasRef) { this.redraw(); }
  }

  private isShapeTool(): boolean {
    return this.tool === 'line' || this.tool === 'rect' || this.tool === 'circle';
  }

  cellFromEvent(ev: PointerEvent): Cell | null {
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    const x = Math.floor((ev.clientX - rect.left) / rect.width * COLS);
    const y = Math.floor((ev.clientY - rect.top) / rect.height * ROWS);
    if (x < 0 || y < 0 || x >= COLS || y >= ROWS) { return null; }
    return { x, y };
  }

  onPointerDown(ev: PointerEvent) {
    this.pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    this.canvasRef.nativeElement.setPointerCapture(ev.pointerId);
    if (this.pointers.size >= 2) { this.startGesture(); return; }
    const c = this.cellFromEvent(ev);
    if (!c) { return; }
    // Building a selection doesn't change the frame, so don't snapshot undo / record colour.
    if (this.tool !== 'eyedropper' && !this.selectMode) { this.strokeStart.emit(); }
    this.beginStroke(c.x, c.y);
  }
  onPointerMove(ev: PointerEvent) {
    if (this.pointers.has(ev.pointerId)) { this.pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY }); }
    if (this.pointers.size >= 2) { this.updateGesture(); return; }
    if (!this.active) { return; }
    const c = this.cellFromEvent(ev);
    if (c) { this.moveStroke(c.x, c.y); }
  }
  onPointerUp(ev: PointerEvent) {
    this.pointers.delete(ev.pointerId);
    if (this.pointers.size < 2) { this.gesture = null; }
    if (this.active || this.startPt) { this.endStroke(); }
  }

  private startGesture() {
    this.active = false; this.startPt = null;   // cancel any in-progress stroke
    this.redraw();
    const c = this.centroidLocal();
    this.gesture = {
      dist: this.spread(),
      startScale: this.scale,
      contentX: (c.x - this.offX) / this.scale,
      contentY: (c.y - this.offY) / this.scale,
    };
    this.noteZoomUsed();
  }
  private updateGesture() {
    if (!this.gesture) { return; }
    const d = this.spread();
    const next = (this.gesture.dist > 0 && d > 0)
      ? clampScale(this.gesture.startScale * (d / this.gesture.dist), this.minScale, this.maxScale)
      : this.scale;
    // Pinning the anchor point under the fingers handles zoom and pan together:
    // spreading magnifies about that spot, sliding both fingers drags it around.
    const c = this.centroidLocal();
    this.scale = next;
    this.offX = c.x - this.gesture.contentX * next;
    this.offY = c.y - this.gesture.contentY * next;
    this.clampView();
  }

  beginStroke(x: number, y: number) {
    this.active = true;
    this.startPt = { x, y };
    this.lastPt = { x, y };
    if (this.selectMode) {
      switch (this.tool) {
        case 'pencil': this.addToSelection([{ x, y }]); break;
        case 'eraser': this.removeFromSelection([{ x, y }]); break;
        case 'row': this.addToSelection(rowCells(y)); this.active = false; break;
        case 'col': this.addToSelection(colCells(x)); this.active = false; break;
        // rect/circle wait for endStroke; line/fill/eyedropper: no-op
      }
      return;
    }
    switch (this.tool) {
      case 'eyedropper': this.colorPicked.emit(this.frame[idx(x, y)]); this.active = false; break;
      case 'fill': this.commitFrame(floodFill(this.frame, x, y, this.color)); this.active = false; break;
      case 'row': this.commitCells(rowCells(y)); this.active = false; break;
      case 'col': this.commitCells(colCells(x)); this.active = false; break;
      case 'pencil': this.paintCell(x, y, normalizeHex(this.color)); break;
      case 'eraser': this.paintCell(x, y, OFF); break;
      // line/rect/circle: wait for move + up
    }
  }

  moveStroke(x: number, y: number) {
    this.lastPt = { x, y };
    if (this.selectMode) {
      if (this.tool === 'pencil') { this.addToSelection([{ x, y }]); return; }
      if (this.tool === 'eraser') { this.removeFromSelection([{ x, y }]); return; }
      if ((this.tool === 'rect' || this.tool === 'circle') && this.startPt) {
        this.previewSelection(this.selectionCellsForTool(this.startPt.x, this.startPt.y, x, y));
      }
      return;
    }
    if (this.tool === 'pencil') { this.paintCell(x, y, normalizeHex(this.color)); return; }
    if (this.tool === 'eraser') { this.paintCell(x, y, OFF); return; }
    if (this.isShapeTool() && this.startPt) {
      this.preview(cellsForTool(this.tool, this.startPt.x, this.startPt.y, x, y));
    }
  }

  endStroke() {
    if (this.selectMode) {
      if ((this.tool === 'rect' || this.tool === 'circle') && this.startPt && this.lastPt) {
        this.addToSelection(this.selectionCellsForTool(this.startPt.x, this.startPt.y, this.lastPt.x, this.lastPt.y));
      }
      this.active = false; this.startPt = null; this.lastPt = null;
      return;
    }
    if (this.isShapeTool() && this.startPt && this.lastPt) {
      this.commitCells(cellsForTool(this.tool, this.startPt.x, this.startPt.y, this.lastPt.x, this.lastPt.y));
    }
    this.active = false;
    this.startPt = null;
    this.lastPt = null;
  }

  private paintCell(x: number, y: number, value: string) {
    if (this.frame[idx(x, y)] === value) { return; }
    this.frame = this.frame.slice();
    this.frame[idx(x, y)] = value;
    this.selfFrame = this.frame;
    this.frameChange.emit(this.frame);
    this.drawCell(x, y, value);
  }
  private commitCells(cells: Cell[]) {
    this.frame = applyCells(this.frame, cells, this.color);
    this.selfFrame = this.frame;
    this.frameChange.emit(this.frame);
    this.redraw();
  }
  private commitFrame(f: Frame) {
    this.frame = f;
    this.selfFrame = this.frame;
    this.frameChange.emit(this.frame);
    this.redraw();
  }
  private preview(cells: Cell[]) {
    this.redraw();
    const ctx = this.canvasRef?.nativeElement.getContext('2d');
    if (!ctx) { return; }
    const v = normalizeHex(this.color);
    for (const c of cells) { this.drawCellCtx(ctx, c.x, c.y, v); }
  }

  redraw() {
    const ctx = this.canvasRef?.nativeElement.getContext('2d');
    if (!ctx) { return; }
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) { this.drawCellCtx(ctx, x, y, this.frame[idx(x, y)]); }
    }
    if (this.selection.size) {
      ctx.fillStyle = 'rgba(0, 204, 255, 0.4)';
      this.selection.forEach(i => {
        const x = i % COLS, y = Math.floor(i / COLS);
        ctx.fillRect(x * this.cell, y * this.cell, this.cell, this.cell);
      });
    }
    this.drawCursors(ctx);
  }

  /** Outline, never fill: someone else's brush must not hide the art under it. */
  private drawCursors(ctx: CanvasRenderingContext2D) {
    if (!this.cursors?.length) { return; }
    ctx.lineWidth = Math.max(2, Math.round(this.cell / 5));
    for (const c of this.cursors) {
      if (typeof c?.cell !== 'number' || c.cell < 0 || c.cell >= COLS * ROWS) { continue; }
      const x = (c.cell % COLS) * this.cell, y = Math.floor(c.cell / COLS) * this.cell;
      ctx.strokeStyle = c.color || '#ffffff';
      ctx.strokeRect(x + 1, y + 1, this.cell - 2, this.cell - 2);
    }
  }
  private drawCell(x: number, y: number, color: string) {
    const ctx = this.canvasRef?.nativeElement.getContext('2d');
    if (ctx) { this.drawCellCtx(ctx, x, y, color); }
  }
  private drawCellCtx(ctx: CanvasRenderingContext2D, x: number, y: number, color: string) {
    ctx.fillStyle = color === OFF ? '#111111' : color;
    ctx.fillRect(x * this.cell, y * this.cell, this.cell, this.cell);
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.strokeRect(x * this.cell + 0.5, y * this.cell + 0.5, this.cell, this.cell);
  }
}
